import {isBefore} from 'date-fns';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Form,
  FormButton,
  FormField,
  Separator,
  Tooltip,
  type FormSubmitHandler,
} from 'platform/components';
import {Box, Grid, GridItem, HStack, Icon, Show, Text, VStack} from 'platform/foundation';

import {useState} from 'react';

import {useInspectionListQuery, InspectionType} from '@dms/api/core';
import {
  useGenerateDescriptionMutation,
  useGetVehicleQuery,
  type PublishAuctionRequestBody,
} from '@dms/api/sales';
import {useGetDescriptionGeneratorConfigQuery} from '@dms/api/settings';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';
import {handleApiError, useBranches, useVehicleCatalogue} from '@dms/shared';
import {DigitalCertificateLink} from '@dms/shared-aftersales';

import {ApiError, composePath, getApiDateString, parseDate} from 'shared/web';

import {
  auctionDeadlines,
  DEFAULT_BIDDING_DAYS,
  DEFAULT_DECISION_DAYS,
} from '../../model/auctionView';
import type {AuctionRule, AuctionView, DateTimeString} from '../../model/auctionView.types';
import {equipmentTitle} from '../../model/equipmentTitle';
import {AUCTION_TYPES, listingSchema, type ListingFormValues} from '../../model/listingSchema';
import {sanitizeListingHtml} from '../../model/sanitizeListingHtml';
import {useAuctionFormat} from '../components/useAuctionFormat';
import {ListingPreviewDialog} from './ListingPreviewDialog';
import {ListingVehicle} from './ListingVehicle';
import {useListingPhotos} from './useListingPhotos';

const NOTE_TOOLBAR = ['bold', 'italic', 'listBullet'] as const;

// The publish rules that the seller fixes outside the auction tab, with the page where he fixes them.
const BLOCKER_FIX_ROUTE: Partial<
  Record<AuctionRule, typeof vehiclesRoutes.detail | typeof vehiclesRoutes.conditionsInspections>
> = {
  CAR_AUDIT_MISSING: vehiclesRoutes.conditionsInspections,
  VEHICLE_NOT_FOR_SALE: vehiclesRoutes.detail,
};

function PeriodValue(props: {
  label: string;
  tooltip: string;
  days: number;
  until: DateTimeString | Date;
}) {
  const format = useAuctionFormat();
  const until = typeof props.until === 'string' ? parseDate(props.until) : props.until;
  const date = format.date(until.toISOString());
  return (
    <VStack>
      <HStack spacing={1} align="center">
        <Text size="xSmall" color="secondary">
          {props.label}
        </Text>
        <Tooltip description={props.tooltip}>
          <Icon value="action/info_outline" color="text.secondary" size={4} tabIndex={0} />
        </Tooltip>
      </HStack>
      <Text alternative>{i18n.t('entity.auction.settings.days', {count: props.days})}</Text>
      <Text size="xSmall" color="secondary">
        {isBefore(until, new Date())
          ? i18n.t('entity.auction.settings.ended', {date})
          : i18n.t('entity.auction.settings.until', {date})}
      </Text>
    </VStack>
  );
}

interface PreparationStepProps {
  vehicleId: string;
  view: AuctionView;
  onPublish: (body: PublishAuctionRequestBody) => Promise<unknown>;
}

export function PreparationStep(props: PreparationStepProps) {
  const isEditable = props.view.state === 'draft';
  const {data: vehicle} = useGetVehicleQuery({vehicleId: props.vehicleId});
  const {data: inspections, isLoading: isInspectionsLoading} = useInspectionListQuery({
    inspectionListRequestBody: {
      vehicleId: props.vehicleId,
      inspectionType: InspectionType.VALIDATION_INSPECTION,
    },
  });
  const {branchOptions} = useBranches();
  const [catalogue] = useVehicleCatalogue(vehicle?.type);
  const photos = useListingPhotos(props.vehicleId);
  const format = useAuctionFormat();
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [generateDescription, {isLoading: isGeneratingDescription}] =
    useGenerateDescriptionMutation();
  const {data: generatorConfig} = useGetDescriptionGeneratorConfigQuery({});

  // A failed inspection list does not block the listing; the publish blockers explain it.
  if (vehicle === undefined || isInspectionsLoading) {
    return null;
  }

  const minMileage = vehicle.vehicle.mileage ?? 0;
  const finishedInspections = (inspections ?? []).filter(
    ({inspectionState}) => inspectionState === 'finished_audit'
  );
  const listing = props.view.listing;
  const biddingDays = props.view.biddingDays ?? DEFAULT_BIDDING_DAYS;
  const {biddingEndsAt, decisionDeadlineAt} = auctionDeadlines(props.view, new Date());
  const blockers = props.view.blockers.filter(({action}) => action === 'publish');

  const defaultValues: ListingFormValues = listing
    ? {...listing, estimatedAvailabilityAt: listing.estimatedAvailabilityAt.slice(0, 10)}
    : {
        type: 'auction',
        title: vehicle.title,
        description: null,
        inspectionId: finishedInspections[0]?.id ?? null,
        estimatedAvailabilityAt: getApiDateString(new Date()),
        estimatedMileage: vehicle.vehicle.mileage,
        collectionSiteId: vehicle.branchId,
        publicNote: null,
        internalNote: null,
      };

  const generatedTitle = equipmentTitle(
    vehicle.title,
    (vehicle.features ?? []).map((feature) => ({
      ...feature,
      translation: catalogue.getFeature(feature.key) ?? feature.translation,
    }))
  );
  const isGeneratorEnabled = generatorConfig?.enabled === true && !isGeneratingDescription;
  // The tenant settings of the advertising description generator also apply to the auction listing.
  const requestDescription = () =>
    generatorConfig
      ? generateDescription({
          vehicleId: props.vehicleId,
          body: {
            language: generatorConfig.language,
            includeModel: generatorConfig.includeModel,
            includeEngine: generatorConfig.includeEngine,
            includeFeatures: generatorConfig.includeFeatures,
            includeCondition: generatorConfig.includeCondition,
            includeBranch: generatorConfig.includeBranch,
            quickPresetIds: [],
            length: generatorConfig.length,
            mentionText: generatorConfig.mentionText,
            additionalInstruction: generatorConfig.additionalInstruction,
          },
        })
          .unwrap()
          .then(({content}) => content)
          .catch((error: ApiError) => {
            handleApiError(error);
            return null;
          })
      : Promise.resolve(null);

  const handleSubmit: FormSubmitHandler<ListingFormValues> = async (values) => {
    // The schema rejects the empty values, so the publish body has no null in a required field.
    await props.onPublish({
      type: values.type,
      title: values.title,
      description: sanitizeListingHtml(values.description),
      inspectionId: values.inspectionId ?? '',
      estimatedAvailabilityAt: values.estimatedAvailabilityAt ?? '',
      estimatedMileage: values.estimatedMileage ?? minMileage,
      biddingDays,
      collectionSiteId: values.collectionSiteId,
      publicNote: values.publicNote,
      internalNote: values.internalNote,
    });
  };

  return (
    <Form<ListingFormValues>
      key={props.view.roundId ?? 'new'}
      defaultValues={defaultValues}
      experimentalZodSchema={listingSchema(minMileage)}
      mode="onChange"
      isDisabled={!isEditable}
      onSubmit={handleSubmit}
    >
      {(control, formApi) => {
        const setGeneratedDescription = () =>
          requestDescription().then((content) => {
            if (content !== null) {
              formApi.setValue('description', content, {shouldValidate: true});
            }
          });
        const autoFill = () => {
          formApi.setValue('title', generatedTitle, {shouldValidate: true});
          return setGeneratedDescription();
        };

        return (
          <>
            <Grid columns={[1, 1, 5]} spacing={6} align="flex-start">
              <GridItem span={[1, 1, 3]}>
                <VStack spacing={6}>
                  <ListingVehicle vehicleId={props.vehicleId} />
                  {/* A disabled input cuts a long title, so the published title is wrapping text. */}
                  <Show when={!isEditable}>
                    <VStack spacing={1}>
                      <Text size="small" color="secondary">
                        {i18n.t('entity.auction.listing.title')}
                      </Text>
                      <Text overflowWrap="anywhere" data-testid="auction-listing-title-readonly">
                        {formApi.watch('title')}
                      </Text>
                    </VStack>
                  </Show>
                  <Show when={isEditable}>
                    <FormField
                      control={control}
                      type="text"
                      name="title"
                      label={i18n.t('entity.auction.listing.title')}
                      labelActions={[
                        {
                          title: i18n.t('entity.auction.listing.generateTitle'),
                          leftIcon: 'action/magic_wand',
                          // shortcut: placeholder only, enable it when the title generator is designed.
                          isDisabled: true,
                          onClick: () =>
                            formApi.setValue('title', generatedTitle, {shouldValidate: true}),
                        },
                      ]}
                      data-testid="auction-listing-title"
                    />
                  </Show>
                  <FormField
                    control={control}
                    type="richText"
                    name="description"
                    label={i18n.t('entity.auction.listing.description')}
                    labelActions={[
                      {
                        title: i18n.t('entity.auction.listing.generateDescription'),
                        leftIcon: 'action/magic_wand',
                        // shortcut: placeholder only, enable it when the listing generator is designed.
                        isDisabled: true,
                        onClick: () => void setGeneratedDescription(),
                      },
                    ]}
                    toolbarOptions={[...NOTE_TOOLBAR]}
                    minRows={8}
                    data-testid="auction-listing-description"
                  />
                  <Separator spacing={0} />
                  <Box position="relative">
                    <FormField
                      control={control}
                      type="choice"
                      name="inspectionId"
                      label={i18n.t('entity.auction.listing.inspection')}
                      placeholder={i18n.t('entity.auction.listing.selectInspection')}
                      options={finishedInspections.map((inspection) => ({
                        value: inspection.id,
                        label: [
                          i18n.t('entity.auction.listing.carAudit'),
                          inspection.finishedAt ? format.date(inspection.finishedAt) : null,
                        ]
                          .filter(Boolean)
                          .join(' · '),
                      }))}
                      data-testid="auction-listing-inspection"
                    />
                    {/* v11 shows the certificate link on the label row of the inspection field. */}
                    {/* The link button is higher than the label row, so center it on that row. */}
                    <Box position="absolute" top={0} right={0}>
                      <HStack height={4} align="center">
                        <DigitalCertificateLink
                          variant="link"
                          label={i18n.t('entity.auction.listing.displayCertificate')}
                          inspectionId={formApi.watch('inspectionId')}
                        />
                      </HStack>
                    </Box>
                  </Box>
                </VStack>
              </GridItem>
              <GridItem span={[1, 1, 2]}>
                <VStack spacing={4}>
                  <HStack spacing={2} justify="flex-end" wrap>
                    <Button
                      variant="secondary"
                      leftIcon="action/magic_wand"
                      title={i18n.t('entity.auction.listing.autoFill')}
                      isDisabled={!isEditable || photos.length === 0 || !isGeneratorEnabled}
                      isLoading={isGeneratingDescription}
                      onClick={() => void autoFill()}
                      data-testid="auction-auto-fill"
                    />
                    <Button
                      variant="secondary"
                      leftIcon="action/search"
                      title={i18n.t('entity.auction.listing.preview')}
                      onClick={() => setIsPreviewOpen(true)}
                      data-testid="auction-preview"
                    />
                    <FormButton
                      control={control}
                      type="submit"
                      variant="primary"
                      title={i18n.t('entity.auction.listing.publish')}
                      isDisabled={
                        !isEditable ||
                        !props.view.allowedActions.includes('publish') ||
                        photos.length === 0
                      }
                      data-testid="publish-auction"
                    />
                  </HStack>
                  <Show when={isEditable && blockers.length > 0}>
                    <PublishBlockers blockers={blockers} vehicleId={props.vehicleId} />
                  </Show>
                  <Card
                    variant="inlineGrey"
                    title={i18n.t('entity.auction.settings.title')}
                    data-testid="auction-settings"
                  >
                    <VStack spacing={4}>
                      <FormField
                        control={control}
                        type="choice"
                        name="type"
                        label={i18n.t('entity.auction.settings.auctionType')}
                        isNotClearable
                        options={AUCTION_TYPES.map((type) => ({
                          value: type,
                          label: i18n.t(`entity.auction.settings.type.${type}`),
                        }))}
                      />
                      <AuctionAudience />
                      <Grid columns={2} spacing={4}>
                        <PeriodValue
                          label={i18n.t('entity.auction.settings.biddingPeriod')}
                          tooltip={i18n.t('entity.auction.settings.biddingPeriodHint', {
                            days: i18n.t('entity.auction.settings.days', {count: biddingDays}),
                          })}
                          days={biddingDays}
                          until={biddingEndsAt}
                        />
                        <PeriodValue
                          label={i18n.t('entity.auction.settings.decisionWindow')}
                          tooltip={i18n.t('entity.auction.settings.decisionWindowHint', {
                            days: i18n.t('entity.auction.settings.days', {
                              count: DEFAULT_DECISION_DAYS,
                            }),
                          })}
                          days={DEFAULT_DECISION_DAYS}
                          until={decisionDeadlineAt}
                        />
                      </Grid>
                      <FormField
                        control={control}
                        type="apiDate"
                        name="estimatedAvailabilityAt"
                        label={i18n.t('entity.auction.settings.estimatedAvailability')}
                        minDate={new Date()}
                      />
                      <FormField
                        control={control}
                        type="integer"
                        name="estimatedMileage"
                        label={i18n.t('entity.auction.settings.estimatedMileage')}
                        suffix={i18n.t('general.metric.km')}
                      />
                      <FormField
                        control={control}
                        type="choice"
                        name="collectionSiteId"
                        label={i18n.t('entity.auction.settings.collectionSite')}
                        options={branchOptions}
                      />
                      <FormField
                        control={control}
                        type="richText"
                        name="publicNote"
                        label={i18n.t('entity.auction.settings.publicNote')}
                        helperText={i18n.t('entity.auction.settings.publicNoteHint')}
                        toolbarOptions={[...NOTE_TOOLBAR]}
                      />
                      <FormField
                        control={control}
                        type="richText"
                        name="internalNote"
                        label={i18n.t('entity.auction.settings.internalNote')}
                        helperText={i18n.t('entity.auction.settings.internalNoteHint')}
                        toolbarOptions={[...NOTE_TOOLBAR]}
                      />
                    </VStack>
                  </Card>
                </VStack>
              </GridItem>
            </Grid>
            <ListingPreviewDialog
              isOpen={isPreviewOpen}
              vehicleId={props.vehicleId}
              title={formApi.watch('title')}
              description={formApi.watch('description')}
              publicNote={formApi.watch('publicNote')}
              biddingDays={biddingDays}
              decisionDays={DEFAULT_DECISION_DAYS}
              estimatedAvailabilityAt={formApi.watch('estimatedAvailabilityAt')}
              collectionSiteId={formApi.watch('collectionSiteId')}
              estimatedMileage={formApi.watch('estimatedMileage')}
              inspectionId={formApi.watch('inspectionId')}
              onClose={() => setIsPreviewOpen(false)}
            />
          </>
        );
      }}
    </Form>
  );
}

// shortcut: the BE stores no audience yet and v1 sends every round to the Fastback network only. Make it a form field when the publish request accepts an audience.
function AuctionAudience() {
  return (
    <VStack spacing={2}>
      <Text size="xSmall" color="secondary">
        {i18n.t('entity.auction.settings.audience')}
      </Text>
      <Box padding={3} borderRadius="small" border="1px solid" borderColor="general.separator">
        <Checkbox
          value
          isDisabled
          label={i18n.t('entity.auction.settings.fastback')}
          helperText={i18n.t('entity.auction.settings.fastbackHint')}
        />
      </Box>
      <Box padding={3} borderRadius="small" border="1px solid" borderColor="general.separator">
        <Checkbox
          value={false}
          isDisabled
          label={i18n.t('entity.auction.settings.localTraders')}
          helperText={i18n.t('entity.auction.settings.localTradersNotInV1', {
            defaultValue: 'Not in v1',
          })}
        />
      </Box>
    </VStack>
  );
}

function PublishBlockers(props: {blockers: AuctionView['blockers']; vehicleId: string}) {
  return (
    <VStack spacing={2} data-testid="auction-publish-blockers">
      {props.blockers.map((blocker) => {
        const fixRoute = BLOCKER_FIX_ROUTE[blocker.code];
        return (
          <Alert
            key={blocker.code}
            type="inline"
            variant="error"
            message={blocker.message}
            hyperlinks={
              fixRoute
                ? [
                    {
                      title: i18n.t('entity.auction.listing.fix'),
                      href: composePath(fixRoute, {
                        params: {id: props.vehicleId},
                        isSearchParamsPreserved: false,
                      }),
                    },
                  ]
                : undefined
            }
            data-testid={`auction-blocker-${blocker.code}`}
          />
        );
      })}
    </VStack>
  );
}
